import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Input from 'components/shared/form/Input';
import StandardModal from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import Summarization from 'models/Summarization';
import { summarizationQueryParams, updateSummarization } from 'services/api/summarizations';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type EditPatientInformationModalProps = {
  summarization: Summarization;
  onCancel: () => void;
};

export default function EditPatientInformationModal({ summarization, onCancel }: EditPatientInformationModalProps) {
  const patient = summarization.patient ?? {};
  const [name, setName] = useState(patient.name || '');
  const [dateOfBirth, setDateOfBirth] = useState(patient.dateOfBirth || '');
  const [gender, setGender] = useState(patient.gender || '');

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate: updateSummary, isPending } = useMutateWithToast({
    mutationFn: updateSummarization,
  });

  const handleUpdate = () => {
    updateSummary(
      {
        ...summarizationQueryParams.show(summarization.id),
        patient: {
          name,
          dateOfBirth,
          gender,
        },
      },
      {
        onSuccess: () => {
          addToast({ text: 'Patient information was successfully updated!' });
          invalidateQueries({ summarizations: true });
          onCancel();
        },
      }
    );
  };

  return (
    <StandardModal onCancel={onCancel} title='Edit Patient Information'>
      <Content>
        <InputGroup>
          <BodySmall>Patient Name</BodySmall>
          <Input
            type='text'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Enter patient name'
            disabled={isPending}
          />
        </InputGroup>

        <InputGroup>
          <BodySmall>Date of Birth</BodySmall>
          <Input
            type='text'
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            placeholder='Enter date of birth'
            disabled={isPending}
          />
        </InputGroup>

        <InputGroup>
          <BodySmall>Gender</BodySmall>
          <Input
            type='text'
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            placeholder='Enter gender'
            disabled={isPending}
          />
        </InputGroup>
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleUpdate} loading={isPending} disabled={isPending}>
          Update
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled.div`
  margin: 16px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
